// src/components/chat/ChatWidget.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ChatApp from './ChatApp';
import { chatAnalytics } from '../../utils/analytics';

const WidgetContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 2147483647;
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
  }
`;

const ClickCatcher = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>`
  position: absolute;
  width: 80px;
  height: 80px;
  top: -10px;
  left: -10px;
  z-index: 2147483646;
  cursor: pointer;
  display: ${props => props.isOpen ? 'none' : 'block'};
  background: transparent;
  pointer-events: ${props => props.isOpen ? 'none' : 'auto'};
`;

const ChatButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4A55A2 0%, #7B66FF 100%) !important;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(74, 85, 162, 0.3);
  display: ${props => props.isOpen ? 'none' : 'flex'};
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  color: white;
  font-size: 24px;
  position: relative;
  z-index: 2147483647;
  pointer-events: auto !important;
  outline: none;
  touch-action: manipulation;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(74, 85, 162, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ChatWindow = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  right: 10px;
  width: 400px;
  height: 600px;
  max-height: 100vh;
  background: white;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  display: ${props => props.isOpen ? 'block' : 'none'};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  z-index: 2147483646;
  
  @media (max-width: 768px) {
    bottom: 0;
    right: 0px;
    width: 350px;
    max-width: calc(100vw - 20px);
    height: 80vh;
    max-height: 600px;
  }
`;

interface ChatWidgetProps {
  headerConfig?: {
    title?: string;
    subtitle?: string;
    logoUrl?: string;
    backgroundColor?: string;
    textColor?: string;
    showPulse?: boolean;
  };
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ headerConfig }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    console.log('🔴 State changed to:', isOpen);
    // Notify parent window about state change
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'chatState',
        isOpen: isOpen
      }, '*');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'toggle') {
        console.log('🟣 Received toggle message from parent');
        setIsOpen(prev => !prev);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Send initial state to parent
  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'chatState',
        isOpen: false
      }, '*');
    }
  }, []);

  const handleToggle = () => {
    console.log('🔵 Toggle clicked! Current state:', isOpen);
    const newState = !isOpen;
    setIsOpen(newState);
    
    // Track chat state changes
    if (newState) {
      chatAnalytics.trackChatOpen();
    } else {
      chatAnalytics.trackChatClose();
    }
  };

  console.log('🔶 Rendering ChatWidget with isOpen:', isOpen);

  return (
    <WidgetContainer id="nexly-chat-widget">
      <ChatWindow isOpen={isOpen}>
        <ChatApp 
          headerConfig={{...headerConfig, showPulse: false}} 
          onClose={handleToggle}
          isOpen={isOpen}
        />
      </ChatWindow>
      <ClickCatcher isOpen={isOpen} onClick={handleToggle} />
      <ChatButton 
        type="button"
        isOpen={isOpen}
        onClick={handleToggle}
      >
        💬
      </ChatButton>
    </WidgetContainer>
  );
};